import { Badge, Box, Button, Center, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import { ListLoadError } from "../../../common/ListLoadError";
import { useMayCreateContract } from "./useMayCreateContract";
import { useAwardMayBeWithdrawn } from "../FrameworkNoLongerActive";
import { useState } from "react";
import { FiAward, FiExternalLink, FiFileText } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import {
  CallOffMechanism,
  FrameworkAgreementLinkDto,
  FrameworkAgreementType,
  ReceivedBidDto,
  SourcingEventStatus,
  SourcingEventDto,
  StandstillExemption,
  useGetEvaluationQuery,
  useListBidsForEventQuery,
  useListFrameworkAgreementsQuery,
} from "../../../autogen/bff-api";
import { useFinalStageRound } from "../rounds/RoundSelectionContext";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { formatTimeLeft, timeLeftUntil } from "../../../common/formatting/timeLeft";
import { useCurrentMoment } from "../../sourcing-events/portal/utils/useCurrentMoment";
import { contractUrl } from "../contractUrl";
import { urls } from "../../../urls";
import { bidDocuments } from "./bidDocuments";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardDecisionHistory } from "./AwardDecisionHistory";
import { AwardSavingLine } from "./AwardSavingLine";
import { EventValueSection } from "./EventValueSection";
import { AwardLetterWorkbench } from "./AwardLetterWorkbench";
import { CreateContractFromBidModal } from "./CreateContractFromBidModal";
import { CreateFrameworkAgreementModal, callOffMechanismFor } from "./CreateFrameworkAgreementModal";
import { isDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";
import { relativeAdvantagesLabel } from "./relativeAdvantagesLabel";
import { RevokeAwardModal } from "./RevokeAwardModal";
import { WinnerDocumentationCheck } from "./WinnerDocumentationCheck";
import { awaitsWinnerDocumentationCheck, noStandstillStatus } from "../now/deriveNowPanel";
import { REVEAL_ANCHORS } from "../revealAnchor";

export const AwardOutcome = ({ event, readOnly = false }: { event: SourcingEventDto; readOnly?: boolean }) => {
  const procedureType = event.eventContext.type === "Public" ? event.eventContext.procedureType : null;
  const designContest = isDesignContest(procedureType);
  const offersContract = useMayCreateContract(event) && !designContest;
  const callOffRefused = useAwardMayBeWithdrawn(event);
  const { t } = useTranslation();
  const finalStageRound = useFinalStageRound();
  const navigate = useNavigate();
  const displayDateWithTime = useDisplayDateWithTime();
  const { data, isLoading: areBidsLoading } = useListBidsForEventQuery({ eventId: event.id });
  const {
    currentData: evaluation,
    isError: isEvaluationError,
    isFetching: isReloadingEvaluation,
    refetch: reloadEvaluation,
  } = useGetEvaluationQuery({ eventId: event.id, round: finalStageRound?.id });
  const [contractModalBidId, setContractModalBidId] = useState<string | null>(null);
  const [isRevokeModalOpen, setRevokeModalOpen] = useState(false);
  const [isFrameworkModalOpen, setFrameworkModalOpen] = useState(false);

  const award = event.award;
  const bids = data?.bids ?? [];
  const winningBidIds = award?.winningBidIds ?? [];
  const winners = winningBidIds
    .map((id) => bids.find((b) => b.id === id))
    .filter((b): b is ReceivedBidDto => !!b);
  const contractModalBid = winners.find((w) => w.id === contractModalBidId);
  const isDecision = event.status === SourcingEventStatus.AwardDecision;
  const awaitsDocumentationCheck = awaitsWinnerDocumentationCheck(event);

  const frameworkChoice =
    event.eventContext.type === "Public" ? event.eventContext.frameworkAgreement : undefined;
  const isFrameworkAward = !!frameworkChoice && frameworkChoice !== FrameworkAgreementType.None;
  const { data: frameworkAgreements } = useListFrameworkAgreementsQuery({}, { skip: !isFrameworkAward });
  const listedFramework = (frameworkAgreements?.frameworkAgreements ?? []).find(
    (f) => f.sourcingEventId === event.id,
  );
  const existingFramework =
    event.frameworkAgreementLink ??
    (listedFramework ? { frameworkAgreementId: listedFramework.id, mayOpen: true } : undefined);

  const standstillExpiresAt = award?.standstillExpiresAt ?? undefined;
  const standstillDispatched = standstillExpiresAt !== undefined;
  const standstillExemption = award?.standstillExemption ?? null;

  const now = useCurrentMoment();
  const standstillElapsed =
    standstillExpiresAt !== undefined && now.valueOf() >= Date.parse(standstillExpiresAt);
  const standstillTimeLeft =
    standstillExpiresAt != null ? timeLeftUntil(standstillExpiresAt, now) : null;

  return (
    <Stack spacing="6">
      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="6">
        <Text fontWeight="semibold" fontSize="sm" color="smMuted" textTransform="uppercase" mb="3">
          {designContest ? t("Winners") : t("Awarded suppliers")}
        </Text>
        <Stack spacing="2">
          {areBidsLoading ? (
            <Center py="2">
              <Spinner size="sm" color="smPrimary" />
            </Center>
          ) : winners.length > 0 ? (
            winners.map((winner) => (
              <Box key={winner.id}>
                <HStack spacing="3">
                  <FiAward color="var(--chakra-colors-smPrimary)" />
                  <Text fontWeight="semibold" fontSize="lg" color="smText">
                    {winner.ownerName || t("Unknown")}
                  </Text>
                  <Badge colorScheme="teal">{t("Winner")}</Badge>
                </HStack>
                <AwardSavingLine saving={award?.savings?.find((saving) => saving.bidId === winner.id)} />
              </Box>
            ))
          ) : (
            <Text color="smMuted">{t("Unknown")}</Text>
          )}
        </Stack>
        {award?.relativeAdvantages && (
          <Box mt="4">
            <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase">
              {relativeAdvantagesLabel(t, designContest)}
            </Text>
            <Text color="smText" whiteSpace="pre-wrap" overflowWrap="anywhere">
              {award.relativeAdvantages}
            </Text>
          </Box>
        )}
        {award?.rankingDepartureJustification && (
          <Box mt="4">
            <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase">
              {t("Why the award goes to a tender that isn't ranked first")}
            </Text>
            <Text color="smText" whiteSpace="pre-wrap" overflowWrap="anywhere">
              {award.rankingDepartureJustification}
            </Text>
          </Box>
        )}
        {award?.internalNote && (
          <Box mt="4">
            <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase">
              {t("Internal note")}
            </Text>
            <Text color="smText" whiteSpace="pre-wrap">
              {award.internalNote}
            </Text>
          </Box>
        )}
      </Box>

      {!designContest && <EventValueSection event={event} />}

      {isEvaluationError && !evaluation ? (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="4">
          <ListLoadError
            message={t("Could not load the evaluation.")}
            onRetry={reloadEvaluation}
            isRetrying={isReloadingEvaluation}
          />
        </Box>
      ) : (
        evaluation &&
        evaluation.bids.length > 0 && (
          <AwardBidsTable evaluation={evaluation} mode="confirmed" selectedBidIds={winningBidIds} />
        )
      )}

      <Box id={REVEAL_ANCHORS.awardLetters} sx={{ scrollMarginTop: "1rem" }}>
        <AwardLetterWorkbench event={event} />
      </Box>

      {event.winnerDocumentation && (
        <WinnerDocumentationCheck
          event={event}
          check={event.winnerDocumentation}
          chosenSuppliers={winners.map((winner) => winner.ownerName).filter((name): name is string => !!name)}
          readOnly={readOnly}
        />
      )}

      {isDecision ? (
        <Box bg="smBackgroundSecondary" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
          {standstillDispatched && (standstillExemption || standstillElapsed) && awaitsDocumentationCheck ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Record the documentation check above before you confirm the award.")}
            </Text>
          ) : standstillExemption ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {!standstillDispatched
                ? t("Send the award letters before you confirm the award")
                : noStandstillText(event, standstillExemption, t)}
            </Text>
          ) : !standstillDispatched ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Send the award letters to start the standstill period before confirming the award.")}
            </Text>
          ) : standstillElapsed ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("The standstill period has passed. You can now confirm the award.")}
            </Text>
          ) : (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Standstill period ends {{date}} ({{timeLeft}}). The award can be confirmed after this.", {
                date: standstillExpiresAt ? displayDateWithTime(standstillExpiresAt) : "",
                timeLeft: standstillTimeLeft ? formatTimeLeft(standstillTimeLeft, t) : "",
              })}
            </Text>
          )}
          {!readOnly && (
            <Button variant="outline" colorScheme="red" onClick={() => setRevokeModalOpen(true)}>
              {t("Change award decision")}
            </Button>
          )}
        </Box>
      ) : isFrameworkAward ? (
        <FrameworkAwardOutcome
          winnerCount={new Set(winners.map((w) => w.owner)).size}
          existingFramework={existingFramework}
          readOnly={readOnly}
          mayCreate={offersContract}
          onCreate={() => setFrameworkModalOpen(true)}
        />
      ) : (
        <Stack spacing="3">
          {winners.map((winner) => {
            const { contractId } = winner;
            return (
              <Box key={winner.id} bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
                <HStack justify="space-between">
                  <HStack spacing="3">
                    <FiFileText />
                    <Text color="smText">{winner.ownerName || t("Unknown")}</Text>
                  </HStack>
                  {contractId ? (
                    <Button
                      variant="outline"
                      colorScheme="teal"
                      rightIcon={<FiExternalLink />}
                      onClick={() => navigate(contractUrl(contractId, winner.contractState, event.id))}
                    >
                      {t("Go to contract")}
                    </Button>
                  ) : callOffRefused ? (
                    <Text fontSize="sm" color="smMuted">
                      {t("The framework agreement is no longer active.")}
                    </Text>
                  ) : readOnly ? (
                    <Text fontSize="sm" color="smMuted">
                      {t("No contract")}
                    </Text>
                  ) : offersContract ? (
                    <Button colorScheme="teal" onClick={() => setContractModalBidId(winner.id)}>
                      {t("Create contract")}
                    </Button>
                  ) : null}
                </HStack>
              </Box>
            );
          })}
        </Stack>
      )}

      <AwardDecisionHistory event={event} />

      {isDecision && !readOnly && (
        <RevokeAwardModal
          eventId={event.id}
          lettersDispatched={standstillDispatched}
          isOpen={isRevokeModalOpen}
          onClose={() => setRevokeModalOpen(false)}
        />
      )}

      {isFrameworkModalOpen && (
        <CreateFrameworkAgreementModal
          eventId={event.id}
          defaultTitle={event.eventData.title}
          defaultMechanism={callOffMechanismFor(frameworkChoice)}
          supplierCount={new Set(winners.map((w) => w.owner)).size}
          isOpen={isFrameworkModalOpen}
          onClose={() => setFrameworkModalOpen(false)}
        />
      )}

      {contractModalBid && (
        <CreateContractFromBidModal
          eventId={event.id}
          bidId={contractModalBid.id}
          supplierName={contractModalBid.ownerName}
          defaultTitle={event.eventData.title}
          procurementDocuments={event.eventData.documents ?? []}
          bidDocuments={bidDocuments(contractModalBid)}
          isOpen={contractModalBidId !== null}
          onClose={() => setContractModalBidId(null)}
        />
      )}
    </Stack>
  );
};

const FrameworkAwardOutcome = ({
  winnerCount,
  existingFramework,
  readOnly,
  mayCreate,
  onCreate,
}: {
  winnerCount: number;
  existingFramework?: FrameworkAgreementLinkDto;
  readOnly: boolean;
  mayCreate: boolean;
  onCreate: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
      <HStack justify="space-between" spacing="4" flexWrap="wrap" rowGap="3">
        <Stack spacing="0">
          <Text fontWeight="semibold">{t("Framework agreement")}</Text>
          <Text fontSize="sm" color="smMuted">
            {existingFramework
              ? t("The framework agreement and its supplier contracts have been created.")
              : t("Creates the framework agreement and one contract for each of the {{count}} awarded suppliers.", {
                  count: winnerCount,
                })}
          </Text>
        </Stack>
        {existingFramework ? (
          existingFramework.mayOpen && (
            <Button
              variant="outline"
              colorScheme="teal"
              rightIcon={<FiExternalLink />}
              onClick={() => navigate(urls.app.frameworkAgreements.view.go(existingFramework.frameworkAgreementId))}
            >
              {t("Go to framework agreement")}
            </Button>
          )
        ) : readOnly ? (
          <Text fontSize="sm" color="smMuted">
            {t("No framework agreement")}
          </Text>
        ) : mayCreate ? (
          <Button colorScheme="teal" onClick={onCreate}>
            {t("Create framework agreement")}
          </Button>
        ) : (
          <Text fontSize="sm" color="smMuted" maxW="320px">
            {t("Someone with access to create contracts in the department must create the framework agreement.")}
          </Text>
        )}
      </HStack>
    </Box>
  );
};

const noStandstillText = (event: SourcingEventDto, exemption: StandstillExemption, t: TFunction): string => {
  const { key, params } = noStandstillStatus(event, exemption);
  return String(t(key, params ?? {}));
};

